import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Copy, Trash2, RefreshCw } from "lucide-react";
import { api, imageUrl, dateBadge, formatApiError } from "@/lib/api";

const STATUS_COLORS = {
  featured: "bg-[#D4AF37] text-black", regular: "bg-emerald-600 text-white",
  draft: "bg-zinc-600 text-white", hidden: "bg-zinc-800 text-zinc-400",
  sold_out: "bg-orange-600 text-white", cancelled: "bg-red-600 text-white",
  coming_soon: "bg-blue-600 text-white",
};

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const navigate = useNavigate();

  const load = () => api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const duplicate = async (id) => {
    try { const { data } = await api.post(`/admin/events/${id}/duplicate`); toast.success("Duplicated as draft"); navigate(`/admin/events/${data.id}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { const { data } = await api.delete(`/admin/events/${id}`); toast.success(data.message || "Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const repeat = async (id) => {
    try { const { data } = await api.post(`/admin/events/${id}/repeat-next-week`); toast.success(`Rebuilt for ${data.date}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl uppercase text-white">Events</h1>
        <button data-testid="create-event-btn" onClick={() => navigate("/admin/events/new")} className="btn-gold rounded-md px-4 py-2.5 font-oswald text-sm tracking-wide flex items-center gap-1.5"><Plus size={16} /> CREATE EVENT</button>
      </div>

      <div className="space-y-3">
        {events.map((e) => {
          const b = dateBadge(e.date);
          return (
            <div key={e.id} data-testid={`event-row-${e.id}`} className="rounded-xl border border-[#27272A] bg-[#18181B] p-3 flex items-center gap-4">
              <img src={imageUrl(e.flyer_image)} alt="" className="h-16 w-16 rounded-lg object-cover shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-white truncate">{e.name}</p>
                  <span className={`text-[9px] font-oswald tracking-widest px-2 py-0.5 rounded uppercase ${STATUS_COLORS[e.status] || "bg-zinc-700"}`}>{e.status.replace("_", " ")}</span>
                  {e.is_demo && <span className="text-[9px] font-oswald tracking-widest px-2 py-0.5 rounded bg-purple-700 text-white">DEMO</span>}
                  {e.recurring_weekly && <span className="text-[9px] font-oswald tracking-widest px-2 py-0.5 rounded bg-[#27272A] text-[#D4AF37] inline-flex items-center gap-1"><RefreshCw size={9} /> WEEKLY</span>}
                </div>
                <p className="text-xs text-zinc-400 mt-1">{b.month} {b.day} · {e.venue?.name || "No venue"} · {e.tickets_sold || 0} sold</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`repeat-event-${e.id}`} title="Rebuild next week" onClick={() => repeat(e.id)} className="p-2 text-zinc-400 hover:text-[#D4AF37]"><RefreshCw size={16} /></button>
                <button data-testid={`edit-event-${e.id}`} onClick={() => navigate(`/admin/events/${e.id}`)} className="p-2 text-zinc-400 hover:text-white"><Pencil size={16} /></button>
                <button data-testid={`dup-event-${e.id}`} onClick={() => duplicate(e.id)} className="p-2 text-zinc-400 hover:text-white"><Copy size={16} /></button>
                <button data-testid={`del-event-${e.id}`} onClick={() => del(e.id)} className="p-2 text-zinc-400 hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            </div>
          );
        })}
        {events.length === 0 && <div className="rounded-xl border border-[#27272A] p-10 text-center text-zinc-500">No events yet. Create your first event.</div>}
      </div>
    </div>
  );
}
